import React, { useEffect, useMemo, useState } from "react";
import { Button, Alert, Spinner, Card } from "react-bootstrap";
import { BillingClient, FileResponse, PortalRequest, StartCheckoutDto } from "../web-api-client.ts";

type BillingStatus =
  | "pending_setup"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "cancel_scheduled";

type BillingStatusResponse = {
  status: BillingStatus;
  amountCents: number;
  currency: string;
  currentPeriodEnd?: string | null;
  trialEnd?: string | null;
  cancelAtPeriodEnd?: boolean | null;
  cancelAt?: string | null;
  canceledAt?: string | null;
};

async function fileResponseToJson<T>(fr: FileResponse): Promise<T> {
  const text = await fr.data.text();
  return JSON.parse(text) as T;
}

const BillingComplete: React.FC = () => {
  const [data, setData] = useState<BillingStatusResponse | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const billingClient = useMemo(() => new BillingClient(), []);

  async function fetchBillingStatus(): Promise<BillingStatusResponse> {
    const res = await fetch("/api/Billing/status", { credentials: "include" });
    if (!res.ok) throw new Error("Unable to fetch billing status.");
    return res.json();
  }

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchBillingStatus();
        setData(d);
      } catch (e: any) {
        setError(e.message ?? "Failed to load billing status.");
      }
    })();
  }, []);

  async function startCheckout() {
    setError(null);
    setWorking(true);
    try {
      const dto: StartCheckoutDto = { dealerId: undefined as any };
      const fr = await billingClient.billing_StartCheckout(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setWorking(false);
      setError(e.message ?? "Failed to start checkout.");
    }
  }

  async function openPortal() {
    setError(null);
    setWorking(true);
    try {
      const dto: PortalRequest = { dealerId: undefined as any };
      const fr = await billingClient.billing_CreatePortal(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setWorking(false);
      setError(e.message ?? "Failed to open portal.");
    }
  }

  const now = Date.now();
  const currentPeriodEndMs = data?.currentPeriodEnd ? new Date(data.currentPeriodEnd).getTime() : 0;

  // Blocked if pending_setup, past_due, canceled, or cancel_scheduled after period end
  const blocked =
    !data
      ? false
      : data.status === "pending_setup" ||
        data.status === "past_due" ||
        data.status === "canceled" ||
        (data.status === "cancel_scheduled" && now >= currentPeriodEndMs);

  const showCancelScheduledBanner =
    data?.status === "cancel_scheduled" && now < currentPeriodEndMs;

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
      <Card className="p-4" style={{ maxWidth: 560, width: "100%" }}>
        <h3 className="mb-3">Complete Billing</h3>
        <p className="text-muted mb-4">To access QCAR, please complete your subscription.</p>

        {error && <Alert variant="danger">{error}</Alert>}

        {!data ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" role="status" />
            <div>Loading…</div>
          </div>
        ) : showCancelScheduledBanner ? (
          <>
            <Alert variant="info" className="mb-4">
              Your subscription is set to end on{" "}
              <strong>{new Date(data.currentPeriodEnd!).toLocaleString()}</strong>. You have access
              until then. You can resume or update payment details in the portal.
            </Alert>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" disabled={working} onClick={openPortal}>
                Manage in Portal
              </Button>
            </div>
          </>
        ) : blocked ? (
          <div className="d-flex flex-wrap gap-2">
            {(data.status === "pending_setup" || data.status === "canceled") && (
              <Button disabled={working} onClick={startCheckout}>
                Complete Payment
              </Button>
            )}
            {/* For past_due, push them to Portal to update card */}
            {data.status === "past_due" && (
              <Button disabled={working} onClick={openPortal}>
                Update Payment Method
              </Button>
            )}
            {/* Portal is always helpful */}
            <Button variant="outline-secondary" disabled={working} onClick={openPortal}>
              View Invoices / Manage
            </Button>
          </div>
        ) : (
          <Alert variant="success">Your subscription is active. You can now use QCAR.</Alert>
        )}
      </Card>
    </div>
  );
};

export default BillingComplete;
